import React, { useCallback, useContext, useEffect, useState } from "react";
import { Container, Modal, ModalHeader, ModalBody } from "reactstrap";
import { HelmetProvider } from "react-helmet-async";
import { Route, Redirect } from "react-router-dom";

import { useAuth0 } from "./Auth/react-auth0-wrapper";

import defaultSetupConfiguration from "./config/defaultSetupConfiguration";
import { EnvironmentContext } from "./config/EnvironmentContext";
import { environments, menuEnvMap } from "./config/Environment";

import OrganizationRoutes from "./Components/Organizations/OrganizationRoutes";
import AdminRoutes from "./Components/ManageOrganization/AdminRoutes";
import SeasonRoutes from "./Components/Seasons/SeasonRoutes";

import Login from "./Components/Base/Login/Login";
import Header from "./Components/Base/Header/Header";
import Sidedrawer from "./Components/Base/Sidedrawer/Sidedrawer";
import Footer from "./Components/Base/Footer/Footer";
import UserProfile from "./Components/Base/UserProfile/UserProfile";
import LanguageSelect from "./Components/Base/LanguageSelect/LanguageSelect";
import "./App.scss";

import sideLogo from "./Components/Base/Assets/Atrium-Negative.png";
import { StaffContext } from "./config/StaffContext";
import { CheckStaffPermissions } from "./Components/Base/Extras/Utilities/CheckScope";
import { useAccess } from "./Components/Base/Hooks/access";
import { useToken } from "./Components/Base/Hooks/token";

const App = (props) => {
  const { environment, language, locale } = props;
  const { loading, user, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const title = "Competition Admin";
  const setup = defaultSetupConfiguration;
  const [mainState, setMainState] = useState({
    ...JSON.parse(localStorage.getItem("mainState")),
    fluid: false
  });
  const [token, setToken] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [products, setProducts] = useState({});
  const [, updateAllState] = React.useState();
  const forceUpdate = React.useCallback(() => updateAllState({}), []);
  const { productsToken: productsTokenFromUseAccess } = useAccess();
  const { productsToken: productsTokenFromUseToken } = useToken();
  // const [fluidLayout, setFluidLayout] = useState(false);

  function updateState(key, data) {
    let thisState = mainState;
    if (mainState[key] !== data) {
      thisState[key] = data;
      setMainState(thisState);
      localStorage.setItem("mainState", JSON.stringify(thisState));
      forceUpdate();
    }
  }

  const logoutWithRedirect = () => {
    setToken(null);
    setHasAccess(false);
    if (token) {
      localStorage.removeItem("productList_" + token.customerId);
    }
    let tokenName = setup.options.subProduct
      ? setup.options.subProduct.replace(/\s/g, "_").toLowerCase() + "_" + setup.options.productCode + "_tokenDetails"
      : setup.options.productCode + "_tokenDetails";
    localStorage.removeItem(tokenName);
    logout({
      returnTo: window.location.origin
    });
  };

  const isStaffScope = () => {
    if (
      productsTokenFromUseToken?.expiry &&
      productsTokenFromUseAccess?.expiry &&
      productsTokenFromUseAccess?.expiry > productsTokenFromUseToken?.expiry
    ) {
      return CheckStaffPermissions(productsTokenFromUseAccess);
    }
    return CheckStaffPermissions(productsTokenFromUseToken);
  };

  return (
    <EnvironmentContext.Provider value={environment}>
      <StaffContext.Provider value={isStaffScope}>
        <HelmetProvider>
          <Header
            title={title}
            login={loginWithRedirect}
            logout={logoutWithRedirect}
            isAuthenticated={isAuthenticated}
            hasAccess={hasAccess}
            orgId={mainState.orgId}
            userprofile={<UserProfile {...props} user={user} />}
            languageselect={<LanguageSelect setup={setup} locale={locale} language={language} />}
          />
          <Sidedrawer
            logout={logoutWithRedirect}
            isAuthenticated={isAuthenticated}
            hasAccess={hasAccess}
            products={products}
            logo={sideLogo}
            orgId={mainState.orgId}
          />
          {!loading && (
            <Container fluid={mainState.fluid} className="mainbody">
              <Login
                login={loginWithRedirect}
                setup={setup}
                loading={loading}
                isAuthenticated={isAuthenticated}
                setToken={setToken}
                setHasAccess={setHasAccess}
                token={token}
                setProducts={setProducts}
              />
              {isAuthenticated && token && (
                <React.Fragment>
                  <Route exact path="/" render={() => <Redirect to="/select/organization" />} />
                  <OrganizationRoutes {...props} updateState={updateState} title={title} setup={setup} />
                  <AdminRoutes {...props} updateState={updateState} title={title} mainState={mainState} setup={setup} />
                  <SeasonRoutes
                    {...props}
                    updateState={updateState}
                    title={title}
                    mainState={mainState}
                    setup={setup}
                  />
                </React.Fragment>
              )}
            </Container>
          )}
          <Footer setup={setup} />
          {user && <EOLAlert />}
        </HelmetProvider>
      </StaffContext.Provider>
    </EnvironmentContext.Provider>
  );
};

export default App;


const EOL_ALERT_KEY = "eol-message";
const EOL_SESSION_KEY = `alert-dismissed-${EOL_ALERT_KEY}`;

const EOLAlert = () => {
  const env = useContext(EnvironmentContext);
  const cmanUrl = environments.envProductLinksMap?.[menuEnvMap?.[env]]?.cman;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(EOL_SESSION_KEY);
    setVisible(!dismissed);
  }, []);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(EOL_SESSION_KEY, "true");
    setVisible(false);
  }, []);

  return (
    <Modal
      isOpen={visible}
      toggle={dismiss}
      autoFocus
      trapFocus
      returnFocusAfterClose
      keyboard
      backdrop="static"
      labelledBy="eol-notice-title"
      zIndex={10010}
    >
      <ModalHeader toggle={dismiss}>
        <span id="eol-notice-title">Competition Admin will be retired soon.</span>
      </ModalHeader>
      <ModalBody>
        <p>
          Please begin using Competition Management at:{" "}
          <a href={cmanUrl} target="_blank" rel="noopener noreferrer">
            Competition Management
          </a>
          .
        </p>
        <p>
          For help, contact: <a href="mailto:mediasupport@sportradar.com">mediasupport@sportradar.com</a>
        </p>
      </ModalBody>
    </Modal>
  );
};